import React, { useState, useEffect } from 'react';
import { Form, Formik } from 'formik';
import { invoiceStatementConfig } from './formConfig';
import { FormikField } from '../../../../components/FormikField';
import { useTranslation } from 'react-i18next';
import { Button } from 'primereact/button';
import { invoiceStatementInitialValues } from '../../config/initialValues';
import { invoiceStatementValidation } from '../../config/validation';
import styles from './Statement.styles.scss';
import cn from 'classnames';
import DataFieldsDialog from '../../components/DataFieldsDialog/DataFieldsDialog';
import StatementAcknowledgmentDialog from '../../components/StatementAcknowledgmentDialog/StatementAcknowledgmentDialog';
import {
  getStatementStatus,
  requestStatement,
} from '../../../../services/apis';
import { useAuthentication } from '../../../../hooks/useAuthentication';
import OldStatementDialog from '../../components/OldStatementDialog/OldStatementDialog';

const Statement = () => {
  const { t } = useTranslation();
  const { userInfo } = useAuthentication();
  const [showDataFieldsDialog, setShowDataFieldsDialog] = useState(false);
  const [showAcknowledgmentDialog, setShowAcknowledgmentDialog] =
    useState(false);
  const [showOldStatementDialog, setShowOldStatementDialog] = useState(false);
  const [statementStatus, setStatementStatus] = useState(null);
  const [downloadLink, setDownloadLink] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    fetchStatementStatus();
  }, []);

  const fetchStatementStatus = async () => {
    try {
      const response = await getStatementStatus(userInfo.tax_number);
      setStatementStatus(response?.data?.status);
      setDownloadLink(response?.data?.downloadLink);
    } catch (error) {
      console.error('Error fetching statement status:', error);
    }
  };

  const handleDownload = () => {
    if (downloadLink) {
      window.open(downloadLink, '_blank');
    }
  };

  const handleSubmit = async (values) => {
    setIsSubmitting(true);
    try {
      // Flatten savedDataFields object into a single array of field names
      const fields = values.savedDataFields
        ? Object.values(values.savedDataFields).flat()
        : [];
      await requestStatement(
        {
          startDate: values.startDate,
          endDate: values.endDate,
          fields,
          email: values.emailAddress,
          typeOfView: values.typeOfView,
        },
        userInfo.tax_number,
      );
      setShowAcknowledgmentDialog(true);
      fetchStatementStatus();
    } catch (error) {
      console.error('Error requesting statement:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const renderStatementStatus = () => {
    if (!statementStatus) {
      return null;
    }

    const statusConfig = {
      IN_PROGRESS: {
        icon: (
          <i className="pi icn-loading pi-spin" style={{ fontSize: '48px' }} />
        ),
        message: t('invoice_statement.generating'),
        subMessage: t('invoice_statement.generating_sub'),
        showDownload: false,
      },
      READY: {
        icon: (
          <i className="pi icn-file-download" style={{ fontSize: '48px' }} />
        ),
        message: t('invoice_statement.ready'),
        showDownload: true,
      },
      COMPLETED: {
        icon: (
          <i className="pi icn-file-download" style={{ fontSize: '48px' }} />
        ),
        message: t('invoice_statement.ready'),
        showDownload: true,
      },
      FAILED: {
        icon: (
          <i
            className="pi pi-times-circle"
            style={{ fontSize: '48px', color: 'red' }}
          />
        ),
        message: t('invoice_statement.failed'),
        showDownload: false,
      },
    };

    const config = statusConfig[statementStatus];
    if (!config) {
      return null;
    }

    return (
      <div className={cn(styles.statusCard, 'p-4 mb-4')}>
        <div className="text-center">
          {config.icon}
          <p className="mt-3 mb-1 font-weight-bold">{config.message}</p>
          {config.subMessage && (
            <p className="text-muted">{config.subMessage}</p>
          )}
          {config.showDownload && (
            <Button
              className="mt-3"
              label={t('common.download')}
              onClick={handleDownload}
              disabled={!downloadLink}
            />
          )}
        </div>
      </div>
    );
  };

  return (
    <>
      <h1 className={cn(styles.title, 'my-4')}>
        {t('invoice_statement.title')}
      </h1>
      <p className={cn('d-flex gap-2 mb-3', styles.disclaimerText)}>
        <i className="pi icn-info-circle-filled color-theme-primary mr-3" />
        {t('invoice_statement.disclaimer')}
      </p>
      <div className="row">
        <div className="col-md-6">
          <Formik
            validateOnMount
            enableReinitialize
            initialValues={invoiceStatementInitialValues}
            validationSchema={invoiceStatementValidation({ t })}
            onSubmit={(values) => {
              if (downloadLink) {
                setShowOldStatementDialog(true);
              } else {
                handleSubmit(values);
              }
            }}
          >
            {({ values, errors, touched, setFieldValue }) => {
              return (
                <Form className={styles.statementFormContainer}>
                  {invoiceStatementConfig({
                    t,
                    values,
                    toggleFieldsHandler: setShowDataFieldsDialog,
                  }).map((section, index) => (
                    <div
                      className={cn(styles.statementForm)}
                      key={section.fields.length}
                    >
                      <div className="row">
                        {section.fields
                          .filter((item) => !item.hide)
                          .map((field) => (
                            <div className={field.className} key={field.name}>
                              <label
                                htmlFor={field.name}
                                className={
                                  field.required ? styles.requiredLabel : ''
                                }
                              >
                                {field.label}
                              </label>
                              <FormikField
                                name={field.name}
                                component={field.component}
                                error={errors[field.name]}
                                componentProps={{
                                  ...field.componentProps,
                                  className: cn(
                                    field.componentProps?.className,
                                    !values[field.name]
                                      ? styles.emptyField
                                      : '',
                                    errors[field.name] && touched[field.name]
                                      ? styles.errorField
                                      : '',
                                  ),
                                }}
                              />
                            </div>
                          ))}
                      </div>
                    </div>
                  ))}
                  <div>
                    <Button
                      className="px-5 mt-3"
                      type="submit"
                      size="medium"
                      label={t('generate')}
                      loading={isSubmitting}
                      disabled={
                        isSubmitting ||
                        Object.keys(touched).length === 0 ||
                        Object.keys(errors).length > 0
                      }
                    />
                  </div>
                  <DataFieldsDialog
                    isOpen={showDataFieldsDialog}
                    setIsOpen={setShowDataFieldsDialog}
                    initialValues={values.savedDataFields}
                    handleClose={(dataFields) => {
                      setShowDataFieldsDialog(false);
                      setFieldValue('savedDataFields', dataFields);
                    }}
                  />
                  <StatementAcknowledgmentDialog
                    isOpen={showAcknowledgmentDialog}
                    setIsOpen={setShowAcknowledgmentDialog}
                  />
                  <OldStatementDialog
                    isOpen={showOldStatementDialog}
                    setIsOpen={setShowOldStatementDialog}
                    onReject={() => {
                      setShowOldStatementDialog(false);
                    }}
                    onConfirm={() => {
                      setShowOldStatementDialog(false);
                      handleSubmit(values);
                    }}
                  />
                </Form>
              );
            }}
          </Formik>
        </div>
        <div className={cn('col-md-6', styles.status)}>
          {renderStatementStatus()}
        </div>
      </div>
    </>
  );
};

export default React.memo(Statement);
